import { defineMessages } from 'react-intl';

import type { AgentPromptPreset } from '../config';

// Preset blocks shared by several report pages. Texts are written against
// "this report" so they read correctly wherever a report page includes
// them. Sharing descriptors keeps one translation per concept instead of
// one per page.
export const commonMessages = defineMessages({
  keyFindingsTitle: {
    id: 'agentPrompts.common.keyFindingsTitle',
    defaultMessage: 'Key findings',
  },
  keyFindingsPrompt: {
    id: 'agentPrompts.common.keyFindingsPrompt',
    defaultMessage:
      'Summarize the key findings of this report for a clinician.\n\nOutput (grouped by clinical priority):\n1. Findings that need action or follow-up, each with the tooth number, condition, and severity\n2. Findings to monitor, with what change would matter\n3. Normal or negative findings worth documenting\n4. What should be verified clinically before any decision\n\nBase every statement on the report data. Do not invent findings and do not recommend definitive treatment; final decisions stay with the clinician. Answer in the chat; do not create a patient document.',
  },
  patientExplanationTitle: {
    id: 'agentPrompts.common.patientExplanationTitle',
    defaultMessage: 'Patient-friendly explanation',
  },
  patientExplanationPrompt: {
    id: 'agentPrompts.common.patientExplanationPrompt',
    defaultMessage:
      'Draft a patient-friendly explanation of the key findings in this report.\n\nRules:\n- For each finding, explain what it means for the patient, where it is (tooth number plus a plain position description), and what happens if it is not treated.\n- Use everyday words: no radiologic jargon. Describe the process, not the density term.\n- Only cover findings present in this report. Do not add new observations or diagnoses.\n- Note what the clinician still needs to verify chairside.\n\nReply with the explanation in the chat. Do not create a patient document.',
  },
  comparePreviousTitle: {
    id: 'agentPrompts.common.comparePreviousTitle',
    defaultMessage: 'Compare with previous study',
  },
  comparePreviousPrompt: {
    id: 'agentPrompts.common.comparePreviousPrompt',
    defaultMessage:
      'Compare this study with the previous comparable study for this patient.\n\nOutput:\n1. Which studies are compared (type and acquisition dates)\n2. New findings in the latest study\n3. Findings that changed: worsened, improved, or resolved, with tooth numbers\n4. Measurements that changed, with the numeric delta and the interval\n5. What the clinician should review\n\nCompare only same-modality studies. If the studies are not comparable (different type, angle, or quality), say so instead of forcing a comparison. Calculate deltas only from structured values. Answer in the chat; do not create a patient document.',
  },
  summaryDocumentTitle: {
    id: 'agentPrompts.common.summaryDocumentTitle',
    defaultMessage: 'Findings summary document',
  },
  summaryDocumentDetail: {
    id: 'agentPrompts.common.summaryDocumentDetail',
    defaultMessage: 'Publish a patient document based on this report',
  },
  summaryDocumentPrompt: {
    id: 'agentPrompts.common.summaryDocumentPrompt',
    defaultMessage:
      'Create and publish a patient document "Findings summary" for this patient, based on this report — not just a chat answer.\n\nWrite the complete patient document Markdown body: the reason for the examination, the key findings with tooth numbers and dates, the imaging interpretation, and limitations or follow-up considerations.\n\nCreate the document with the patient-document workflow, attach the relevant study images, and publish it.\n\nReply in chat with only a concise summary of the created document. Never paste the document body into the chat.',
  },
  referralDraftTitle: {
    id: 'agentPrompts.common.referralDraftTitle',
    defaultMessage: 'Referral letter',
  },
  referralDraftDetail: {
    id: 'agentPrompts.common.referralDraftDetail',
    defaultMessage: 'Summary of this report for a specialist',
  },
  referralDraftPrompt: {
    id: 'agentPrompts.common.referralDraftPrompt',
    defaultMessage:
      'Create and publish a patient document "Referral letter" based on this report — not just a chat answer.\n\nWrite the complete patient document Markdown body: the reason for referral with the specific clinical question, the relevant findings with tooth numbers and dates, the imaging that supports them, what the specialist should evaluate, and the urgency.\n\nCreate the document with the patient-document workflow, attach the relevant study images, and publish it.\n\nReply in chat with only a concise summary of the created document. Never paste the document body into the chat.',
  },
  followUpDraftTitle: {
    id: 'agentPrompts.common.followUpDraftTitle',
    defaultMessage: 'Patient follow-up message',
  },
  followUpDraftDetail: {
    id: 'agentPrompts.common.followUpDraftDetail',
    defaultMessage: 'Plain-language recap of what happens next',
  },
  followUpDraftPrompt: {
    id: 'agentPrompts.common.followUpDraftPrompt',
    defaultMessage:
      'Draft a patient-friendly follow-up message based on this report and the patient record.\n\nOutput:\n1. Short summary of what was examined or found, in plain words\n2. Simple explanation of the recommended next step\n3. Reassuring closing sentence\n4. Call to action to schedule or continue communication\n\nUse calm, non-technical language. Do not mention diagnoses unless they were explicitly confirmed by the clinician. Reply with the message in the chat. Do not create a patient document.',
  },
});

export const commonPresets = {
  keyFindings: {
    id: 'common.keyFindings',
    group: 'understand',
    title: commonMessages.keyFindingsTitle,
    prompt: commonMessages.keyFindingsPrompt,
  } satisfies AgentPromptPreset,
  patientExplanation: {
    id: 'common.patientExplanation',
    group: 'understand',
    title: commonMessages.patientExplanationTitle,
    prompt: commonMessages.patientExplanationPrompt,
  } satisfies AgentPromptPreset,
  comparePrevious: {
    id: 'common.comparePrevious',
    group: 'understand',
    title: commonMessages.comparePreviousTitle,
    prompt: commonMessages.comparePreviousPrompt,
  } satisfies AgentPromptPreset,
  summaryDocument: {
    id: 'common.summaryDocument',
    group: 'write',
    title: commonMessages.summaryDocumentTitle,
    detail: commonMessages.summaryDocumentDetail,
    prompt: commonMessages.summaryDocumentPrompt,
  } satisfies AgentPromptPreset,
  referralDraft: {
    id: 'common.referralDraft',
    group: 'write',
    title: commonMessages.referralDraftTitle,
    detail: commonMessages.referralDraftDetail,
    prompt: commonMessages.referralDraftPrompt,
  } satisfies AgentPromptPreset,
  followUpDraft: {
    id: 'common.followUpDraft',
    group: 'write',
    title: commonMessages.followUpDraftTitle,
    detail: commonMessages.followUpDraftDetail,
    prompt: commonMessages.followUpDraftPrompt,
  } satisfies AgentPromptPreset,
};
